import {
  Component,
  effect,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { DropdownModule } from 'primeng/dropdown';
import { NgIf } from '@angular/common';
import { SharedModule } from 'primeng/api';
import { GeoDataStore } from '../../../stores/geo/geo-data.state';
import { DataLayerStore } from '../../../stores/data-layer/data-layer.state';
import { FeatureProperties, KeyValueDistricts } from '../../../models/geo-data';
import { getState } from '@ngrx/signals';
import { DataLayer } from '../../../models/data-layer';
import { filterFeaturesBySearchWord } from '../../../stores/geo/geo-utils';
import { ComparisonValueStore } from '../../../stores/comparison-value/comparison-value.state';

@Component({
  selector: 'app-feature-dropdown',
  standalone: true,
  imports: [DropdownModule, NgIf, SharedModule, FormsModule],
  templateUrl: './feature-dropdown.component.html',
  styleUrl: './feature-dropdown.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FeatureDropdownComponent),
      multi: true,
    },
  ],
})
export class FeatureDropdownComponent
  implements ControlValueAccessor, OnInit, OnChanges
{
  readonly geoDataStore = inject(GeoDataStore);
  readonly layerStore = inject(DataLayerStore);
  readonly comparisonValueStore = inject(ComparisonValueStore);

  @Input() label = 'Teilraum / Standort';
  @Input() name = 'feature';
  @Input() selection = '';
  @Input() dataLayer?: DataLayer;
  @Input() dataLayerId = '';
  @Input() autoSelect!: boolean;
  @Input() isStandalone = false;
  @Input() placeholder = 'auswählen ...';
  @Input() allowComparisonValues = false;
  @Output() selectionChange = new EventEmitter<string>();

  districts: FeatureProperties[] | null = null;
  kvDistricts: KeyValueDistricts | null = null;
  touched = false;

  constructor() {
    effect(() => {
      const geoDataState = getState(this.geoDataStore);
      const layerState = getState(this.layerStore);
      this.setDistricts();
    });
  }

  /**
   * Responds to changes in input properties of the component.
   * Specifically, this method tracks changes in the `dataLayerId` property
   * and triggers necessary updates when it changes.
   *
   * @param {SimpleChanges} changes - An object that holds the current and previous values of data-bound properties.
   * @return {void} This method does not return any value.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes['dataLayerId']) {
      this.setDataLayer();
      this.setDistricts();
    }
  }

  /**
   * Initializes the component when it is created. This lifecycle hook is called
   * automatically as part of Angular's component initialization process.
   *
   * During initialization:
   * - Ensures the `dataLayer` is set by invoking `setDataLayer` if it is not already initialized.
   * - If there is a defined `selection`, invokes `onChange` with the current `selection` value.
   *
   * @return {void} No value is returned as this function is used for initialization purposes.
   */
  ngOnInit(): void {
    if (!this.dataLayer) {
      this.setDataLayer();
    }
    if (this.selection) {
      this.onChange(this.selection);
    }
  }

  /**
   * Sets the data layer by retrieving it from the layer store using the specified data layer ID.
   *
   * @return {void} This method does not return a value.
   */
  setDataLayer(): void {
    this.dataLayer = this.layerStore.getById(this.dataLayerId);
  }

  /**
   * Sets districts based on the provided search word and updates the selection and district list accordingly.
   *
   * @param {string} [searchWord] - Optional search keyword used to filter the districts.
   * @return {void} This method does not return any value.
   */
  setDistricts(searchWord?: string): void {
    if (this.dataLayer && this.dataLayer.geoData) {
      this.kvDistricts = this.geoDataStore.getDistrictsById(
        this.dataLayer.geoData
      );
      this.districts = this.kvDistricts
        ? Object.values(
            searchWord
              ? this.filterDistricts(this.kvDistricts, searchWord)
              : this.kvDistricts
          )
        : [];

      if (this.allowComparisonValues) {
        const comparisonValues = this.comparisonValueStore.asArray();
        this.districts = this.districts.concat(comparisonValues);
      }

      if (!this.selection && this.districts && this.autoSelect) {
        this.selection = this.districts[0].key || '';
      } else if (this.selection && this.districts) {
        this.onChange(this.selection);
      }
    }
  }

  protected onChange = (value: string) => {
    setTimeout(() => {
      this.selectionChange.emit(value);
    }, 10);
  };

  private onTouched = () => {
    this.touched = false;
  };

  writeValue(value: any): void {
    this.selection = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  /**
   * Filters the provided districts based on the specified search word.
   *
   * @param {KeyValueDistricts} features - A key-value pair object containing district data to filter.
   * @param {string} searchWord - The text used to filter the district names or related data.
   * @return {KeyValueDistricts} Returns a filtered Object of districts matching the search word criteria.
   */
  filterDistricts(
    features: KeyValueDistricts,
    searchWord: string
  ): KeyValueDistricts {
    return filterFeaturesBySearchWord(features, searchWord);
  }
}
